import React, { useState, useEffect } from 'react';
import { masrafRequestAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import './MasrafTalepleri.css';
import { onayla } from '../utils/onayla';

const MasrafTalepleri = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);

  const isAdmin = () => {
    if (!user) return false;
    const role = user.Role ?? user.role ?? '';
    return role === 'Admin';
  };

  useEffect(() => {
    if (isAdmin()) {
      loadRequests();
    }
  }, [user]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const data = await masrafRequestAPI.getPending();
      setRequests(data);
    } catch (error) {
      console.error('Masraf talepleri yüklenirken hata:', error);
      alert('Masraf talepleri yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await masrafRequestAPI.approve(id);
      await loadRequests();
    } catch (error) {
      console.error('Masraf talebi onaylanırken hata:', error);
      alert('Masraf talebi onaylanırken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleApproveAll = async () => {
    if (requests.length === 0) {
      alert('Onaylanacak masraf talebi yok.');
      return;
    }
    if (!await onayla(`${requests.length} masraf talebini onaylamak istediğinize emin misiniz?`)) return;
    let ok = 0;
    let fail = 0;
    for (const request of requests) {
      try {
        await masrafRequestAPI.approve(request.Id || request.id);
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    await loadRequests();
    alert(fail > 0 ? `${ok} talep onaylandı, ${fail} talepte hata oluştu.` : `${ok} masraf talebi onaylandı.`);
  };

  const handleReject = async (id) => {
    if (!rejectionReason.trim()) {
      alert('Lütfen red nedeni girin!');
      return;
    }

    try {
      await masrafRequestAPI.reject(id, rejectionReason);
      alert('Masraf talebi reddedildi.');
      setShowRejectModal(null);
      setRejectionReason('');
      await loadRequests();
    } catch (error) {
      console.error('Masraf talebi reddedilirken hata:', error);
      alert('Masraf talebi reddedilirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0,00 ₺';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  if (!isAdmin()) {
    return (
      <div className="masraf-talepleri-page">
        <div className="page-header">
          <h1>Masraf Talepleri</h1>
        </div>
        <div className="error-message">
          <p>Bu sayfaya sadece Admin erişebilir.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="masraf-talepleri-page">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Masraf Talepleri</h1>
        {!loading && requests.length > 0 && (
          <button type="button" className="btn btn-success" onClick={handleApproveAll}>
            Tümünü Onayla ({requests.length})
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">Yükleniyor...</div>
      ) : requests.length === 0 ? (
        <div className="empty-state">
          Bekleyen masraf talebi bulunmamaktadır.
        </div>
      ) : (
        <div className="requests-table-container">
          <table className="requests-table">
            <thead>
              <tr>
                <th>Föy No</th>
                <th>Müvekkil</th>
                <th>Talep Eden</th>
                <th>Masraf Türü</th>
                <th>Tutar</th>
                <th>Masraf İstenme Tarihi</th>
                <th>Son Tarih</th>
                <th>Açıklama</th>
                <th>Gelir/Gider</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(request => (
                <tr key={request.Id || request.id}>
                  <td>{request.FoyNo || request.foyNo || '-'}</td>
                  <td>{request.Muvekkil || request.muvekkil || '-'}</td>
                  <td>{request.RequestedByUserName || request.requestedByUserName || '-'}</td>
                  <td>
                    {request.MasrafTuru === 'Diğer' 
                      ? (request.CustomMasrafTuru || request.customMasrafTuru || 'Diğer')
                      : (request.MasrafTuru || request.masrafTuru || '-')}
                  </td>
                  <td>{formatCurrency(request.Tutar || request.tutar)}</td>
                  <td>{formatDate(request.MasrafIstenmeTarihi || request.masrafIstenmeTarihi || request.Tarih || request.tarih)}</td>
                  <td>{formatDate(request.SonTarih || request.sonTarih) || '-'}</td>
                  <td>{request.Aciklama || request.aciklama || '-'}</td>
                  <td>
                    <span className={`badge ${(request.IsGelir || request.isGelir) ? 'badge-success' : 'badge-danger'}`}>
                      {(request.IsGelir || request.isGelir) ? 'Gelir' : 'Gider'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleApprove(request.Id || request.id)}
                      >
                        Onayla
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => setShowRejectModal(request.Id || request.id)}
                      >
                        Reddet
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reddetme Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => {
          setShowRejectModal(null);
          setRejectionReason('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Masraf Talebini Reddet</h3>
            <div className="form-group">
              <label>Red Nedeni *</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows="4"
                placeholder="Red nedeni girin..."
                required
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={() => handleReject(showRejectModal)}
              >
                Reddet
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectionReason('');
                }}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasrafTalepleri;

